import { memo, useEffect, useMemo, useState } from "react";
import { Handle, Position } from "reactflow";
import './nodeStyles.css';
import { Avatar, Typography } from "antd";
import { useSelector } from "react-redux";
import { processString } from "../../utilities/Nodes/getConnectedChildNodes";
import { DescriptionNodeProps } from "../../utilities/types/common";
import { useTranslation } from "react-i18next";
import { saveContent, validateUserInput } from "../../utilities/Nodes/commonFunctions";
import { useNodeDataContext } from "../../context/workbench/DataNodeContext";
import { ActionPopover } from "./CommonComponents";
import { usePermission } from "../../hooks/usePermission";

const DescriptionNode = ({ data, id }: DescriptionNodeProps) => {
    const { t } = useTranslation('common');
    const { profileName, profileImage } = useSelector((state: any) => state.static)
    const dataNodeContext = useNodeDataContext();
    const { canView, canEdit } = usePermission();
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [text, setText] = useState<string>(data?.content || ""); // Initialize from data.content
    const maxLength = 60;
    const content = text ? text : ""; // Use the updated local state for display
    const isLongContent = content?.length > maxLength;
    const isSelected = dataNodeContext.selectedNodeId === id;

    const processedProfileName = useMemo(() => {
        return processString(profileName);
    }, [profileName])

    useEffect(() => {
        setText(data?.content); // Update local state when data changes
    }, [data?.content]);

    const selectNode = () => {
        if (id && data) {
            dataNodeContext.getNodeData(id, data);
        }
    };

    const handleOnchange = (e: any) => {
        let input = e?.target?.value
        setText(validateUserInput(input))
    }

    const handleFocus = (e: any) => {
        const length = e.target.value.length;
        e.target.setSelectionRange(length, length);
        e.target.scrollTop = e.target.scrollHeight
    };

    return (
        <div className={isSelected ? "node question-node selected" : "node question-node"} >
            <div className="node-header d-flex justify-content-between align-items-center">
                <div className="card border-0 w-100 node-inner-card bg-transparent p-0">
                    <div className="card-header bg-transparent border-0 p-0">
                        <div className="d-flex justify-content-between">
                            <div className="d-flex align-items-center mt-2 gap-2">
                                {profileImage ? <Avatar src={profileImage} size={"default"} /> : <Avatar>{processedProfileName}</Avatar>}
                                {/* <span className="user-name">{profileName}</span> */}
                            </div>
                            <div className="d-flex align-items-center mt-2">
                                {canView && canEdit && (
                                    <ActionPopover id={id} data={data} setIsEditing={setIsEditing} />
                                )}
                            </div>
                        </div>
                        {/* <hr className="divider" /> */}
                    </div>
                    <div className="card-body p-0">
                        <p className="inner-title">{t('question')} <br /></p>
                        {isEditing ? (
                            <textarea
                                className="node-textarea mt-2"
                                value={text}
                                onChange={(e) => handleOnchange(e)}
                                onBlur={() => { saveContent(data, id, text); setIsEditing(false) }} // Save content on blur
                                autoFocus
                                rows={3}
                                placeholder={t('questionPlaceholder')}
                                onFocus={handleFocus}
                            />
                        ) : (
                            <>
                                <p className="mb-0 node-content" onClick={() => { canEdit && canView && setIsEditing(true) }}>
                                    {content ? <Typography.Paragraph ellipsis={{ rows: 2 }} className="mb-1">{content}</Typography.Paragraph> : <div className="text-center p-3" onClick={() => { canEdit && canView && setIsEditing(true) }}>{t('workbench.questionNode.questionContent')}</div>}
                                    {isLongContent && (
                                        <span
                                            className="read-more-less"
                                            onClick={(e: any) => { selectNode(); e.stopPropagation(); }}
                                        >
                                            {isSelected ? "" : "Read More"}
                                        </span>
                                    )}
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <Handle type="source" position={Position.Right} className="handle" />
            <Handle type="target" position={Position.Left} className="handle" />
        </div>
    );
};

export default memo(DescriptionNode);
